import { useEffect, useRef, useState } from 'react';
import { acceptedTermsStatus, fetchTermsStatus, freshTermsStatus, recordDeviceTerms, TermsCacheEntry } from './deviceTerms';

/** A prompt belongs to the signed-in account and My Stories, never to authentication. */
export function useTermsStatus(sub: string, active: boolean, cached: TermsCacheEntry | undefined,
  save: (entry: TermsCacheEntry) => void, newRequestId: () => string) {
  const latest = useRef({ cached, save });
  latest.current = { cached, save };
  const session = useRef({ sub, alive: true, deferred: false, saving: false });
  if (session.current.sub !== sub) {
    session.current.alive = false;
    session.current = { sub, alive: true, deferred: false, saving: false };
  }
  const [busy, setBusy] = useState(false);
  const [deferred, setDeferred] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => () => { session.current.alive = false; }, []);
  useEffect(() => {
    session.current.alive = true;
    setBusy(false); setDeferred('');
  }, [sub]);
  useEffect(() => {
    if (!sub || !active) return;
    const current = session.current;
    if (current.deferred || freshTermsStatus(latest.current.cached, sub)) return;
    let cancelled = false;
    void fetchTermsStatus(sub).then(status => {
      if (!cancelled && current.alive && !current.saving) latest.current.save(status);
    }).catch(() => { /* Unknown is not unaccepted. Try again on the next visit. */ });
    return () => { cancelled = true; };
  }, [sub, active, refresh]);
  // Also revalidate on a long-lived My Stories page, at most once per day.
  useEffect(() => {
    if (!active || !freshTermsStatus(cached, sub)) return;
    const timeout = setTimeout(() => setRefresh(value => value + 1),
      Math.max(1, cached.checkedAt + 24 * 60 * 60 * 1000 - Date.now()));
    return () => clearTimeout(timeout);
  }, [active, cached, sub]);
  const needsAcceptance = active && !!sub && deferred !== sub
    && freshTermsStatus(cached, sub) && !cached.accepted;
  const accept = async () => {
    if (!needsAcceptance || session.current.saving || !cached || !('accepted' in cached)) return;
    const current = session.current;
    current.saving = true;
    setBusy(true);
    try {
      const receipt = await recordDeviceTerms(sub, newRequestId(), cached.version);
      if (current.alive) latest.current.save(acceptedTermsStatus(receipt));
    } catch {
      // A tap followed by a failed save still permits this session, without claiming acceptance.
      if (current.alive) { current.deferred = true; setDeferred(sub); }
    } finally {
      current.saving = false;
      if (current.alive) setBusy(false);
    }
  };
  return { needsAcceptance, busy, accept };
}
